import { z } from "zod";
export const PrincipalKind = z.enum(["user", "service_account", "group"]);
export const Principal = z.object({
    kind: PrincipalKind,
    id: z.number().int().positive(),
});
/** `user:12`, `group:3`, `service_account:7` */
export const PrincipalRef = z
    .string()
    .regex(/^(user|service_account|group):\d+$/)
    .transform((s) => {
    const [kind, id] = s.split(":");
    return { kind, id: Number(id) };
});
export const Role = z.enum(["system_admin", "workflow_admin", "project_admin", "member", "guest"]);
export const ScopeKind = z.enum(["workspace", "definition", "container"]);
/** Which scope kind each role takes (PRD 6.2). */
export const ROLE_SCOPE = {
    system_admin: "workspace",
    member: "workspace",
    guest: "workspace",
    workflow_admin: "definition",
    project_admin: "container",
};
export const RoleAssignment = z.object({
    id: z.number().int(),
    principal: Principal,
    role: Role,
    scopeKind: ScopeKind,
    scopeId: z.number().int().nullable(),
    createdAt: z.coerce.date(),
});
export const AssignRoleInput = z
    .object({
    principal: Principal,
    role: Role,
    scopeId: z.number().int().positive().optional(),
})
    .superRefine((v, ctx) => {
    const needsScope = ROLE_SCOPE[v.role] !== "workspace";
    if (needsScope && v.scopeId === undefined) {
        ctx.addIssue({ code: "custom", path: ["scopeId"], message: `${v.role} requires a scopeId` });
    }
    if (!needsScope && v.scopeId !== undefined) {
        ctx.addIssue({ code: "custom", path: ["scopeId"], message: `${v.role} is workspace-scoped; omit scopeId` });
    }
});
export const User = z.object({
    id: z.number().int(),
    identityId: z.uuid(),
    email: z.string(),
    displayName: z.string(),
    active: z.boolean(),
    createdAt: z.coerce.date(),
});
export const AddUserInput = z.object({
    issuer: z.url(),
    subject: z.string().min(1),
    email: z.email(),
    displayName: z.string().min(1).max(200),
    /** workspace-scoped role to grant on creation */
    role: z.enum(["system_admin", "member", "guest"]).default("member"),
});
/** What `/me` returns. */
/** System admins rename, deactivate or reactivate a user. A deactivated user's tokens stop working at once. */
export const UpdateUserInput = z
    .object({ displayName: z.string().min(1).max(200).optional(), active: z.boolean().optional(), baseRole: z.enum(["member", "guest"]).optional() })
    .strict();
export const Me = z.object({
    workspace: z.object({ id: z.uuid(), slug: z.string(), name: z.string() }),
    /** who is acting: a user or a service account */
    actor: Principal,
    user: User.nullable(),
    serviceAccount: z.lazy(() => ServiceAccount).nullable(),
    identity: z.object({ issuer: z.string(), subject: z.string() }).nullable(),
    roles: z.array(RoleAssignment),
    /** every principal the caller acts as: the user plus (from step 5) transitive groups */
    principals: z.array(Principal),
    isSystemAdmin: z.boolean(),
});
// ---------- service accounts & API keys ----------
export const ServiceAccountKey = z
    .string()
    .min(1)
    .max(60)
    .regex(/^[a-z][a-z0-9_-]*$/, "service account key must be lower-case letters, digits, underscores or hyphens");
export const ServiceAccount = z.object({
    id: z.number().int(),
    key: ServiceAccountKey,
    name: z.string(),
    description: z.string(),
    ownerUserId: z.number().int(),
    active: z.boolean(),
    createdAt: z.coerce.date(),
});
export const CreateServiceAccountInput = z.object({
    key: ServiceAccountKey,
    name: z.string().min(1).max(200),
    description: z.string().max(2000).default(""),
    /** workspace role; guests cannot write */
    role: z.enum(["member", "guest"]).default("member"),
});
export const UpdateServiceAccountInput = z.object({ name: z.string().min(1).max(200).optional(), description: z.string().max(2000).optional(), active: z.boolean().optional() }).strict();
export const ApiKey = z.object({
    id: z.number().int(),
    serviceAccountId: z.number().int(),
    label: z.string(),
    keyPrefix: z.string(),
    createdAt: z.coerce.date(),
    expiresAt: z.coerce.date().nullable(),
    lastUsedAt: z.coerce.date().nullable(),
    revokedAt: z.coerce.date().nullable(),
});
export const CreateApiKeyInput = z.object({ label: z.string().max(200).default(""), expiresInDays: z.number().int().min(1).max(3650).optional() });
/** Returned once, at creation. */
export const ApiKeyCreated = ApiKey.extend({ secret: z.string() });
//# sourceMappingURL=identity.js.map